import type { DataPort } from '../../ports/data-port.ts';
import type { Query, Unsubscribe } from '../../ports/query.ts';
import type { Job } from '../../domain/entities/job.ts';

/**
 * The jobs a member is allowed to open, as a query.
 *
 * Reported from dev on 2026-10-30: a client given access to one job
 * signed in, saw **every** job in the company in the portal's project
 * picker, and then saw nothing at all — because the portal landed on
 * the first job alphabetically, which was one they had no right to,
 * and every read beneath it was refused.
 *
 * Both halves of that are this one omission. The `jobs` rule says so
 * out loud:
 *
 * > A list query cannot be filtered per document, so membership and
 * > module level gate it and assignment is enforced by **the client
 * > query** plus the per-document get rule.
 *
 * The client query is this. It did not exist, so nothing enforced it:
 * every job list in the product asked for all of them, and only looked
 * right because internal staff usually hold `jobAccess: 'all'`.
 *
 * **This is a filter, not a boundary.** A rule that lets a client list
 * every job still lets one list every job; this makes the product
 * correct, not the data safe. SCRUM-295 is the rule change that moves
 * the check to the server, and until it lands this is UX enforcement —
 * which `docs/ARCHITECTURE.md` is explicit is not enough on its own.
 */
export interface JobReach {
  readonly jobAccess: 'all' | 'assigned';
  readonly jobIds: readonly string[];
}

/**
 * Firestore serves at most thirty values to an `in`.
 *
 * Above that the query is dropped and the caller filters what comes
 * back — correct results either way, and the alternative is splitting
 * one live subscription into several and merging them, which is a
 * great deal of machinery for a person assigned to thirty-one jobs.
 */
export const MAX_IN = 30;

/**
 * What to ask the database for.
 *
 * - `'everything'` — no constraint needed; they may open any job.
 * - `'nothing'` — assigned to none, so do not ask at all. An `in` with
 *   an empty array is an error in Firestore, and the honest answer is
 *   an empty list rather than a failed read.
 * - a `Query` — constrained to the ids they hold.
 * - `'filter-after'` — more ids than `in` will take; read and filter.
 */
export function jobsVisibleTo(reach: JobReach, extra?: Query): Query | 'nothing' | 'filter-after' {
  if (reach.jobAccess === 'all') return extra ?? {};
  if (reach.jobIds.length === 0) return 'nothing';
  if (reach.jobIds.length > MAX_IN) return 'filter-after';
  return {
    ...extra,
    where: [
      ...(extra?.where ?? []),
      // `id` is mapped to the document id by the Firestore adapter, so
      // no field has to be denormalised onto the job for this.
      { field: 'id', op: 'in', value: [...reach.jobIds] },
    ],
  };
}

/** The same rule applied to rows already in hand. */
export function onlyVisibleJobs<T extends { readonly id: string }>(
  reach: JobReach,
  jobs: readonly T[],
): T[] {
  if (reach.jobAccess === 'all') return [...jobs];
  return jobs.filter((job) => reach.jobIds.includes(job.id));
}

/**
 * Which job to show first.
 *
 * Separated from the list because landing on one they cannot open is
 * the half that made the portal look broken rather than empty: every
 * read under it is refused, and a page of refusals reads as "you have
 * no permissions" rather than "wrong job".
 */
export function firstOpenableJob<T extends { readonly id: string; readonly status?: string }>(
  reach: JobReach,
  jobs: readonly T[],
): T | null {
  const mine = onlyVisibleJobs(reach, jobs);
  // An open job before a closed one: somebody signing in wants the
  // thing being built, not the thing finished last year.
  return mine.find((job) => job.status === 'open') ?? mine[0] ?? null;
}

export interface VisibleJobsContext {
  readonly data: DataPort;
  readonly companyId: string;
}

/**
 * Subscribe to the jobs this member may open, and nothing else.
 *
 * One function rather than the same four lines in fourteen screens —
 * which is how the filter came to be missing from all fourteen. The
 * three awkward cases are handled once:
 *
 * - assigned to none: call back with an empty list and never query, so
 *   nobody has to remember that an empty `in` is an error;
 * - more than `in` will take: query everything and filter what arrives;
 * - otherwise: a constrained query, which is the ordinary path.
 */
export function subscribeToVisibleJobs(
  context: VisibleJobsContext,
  reach: JobReach,
  onChange: (jobs: readonly Job[]) => void,
  onError: (cause: Error) => void,
  extra?: Query,
): Unsubscribe {
  const plan = jobsVisibleTo(reach, extra);
  if (plan === 'nothing') {
    onChange([]);
    return () => {};
  }
  const collection = context.data.collection<Job>({ companyId: context.companyId }, 'jobs');
  if (plan === 'filter-after') {
    return collection.subscribe(
      extra ?? {},
      (page) => onChange(onlyVisibleJobs(reach, page.items)),
      onError,
    );
  }
  return collection.subscribe(plan, (page) => onChange(page.items), onError);
}
